import type { EntityId, PatternSummaryDto, PatternType } from '@miyuki/types';
import {
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@miyuki/ui';
import { BrickWallIcon, CopyIcon, Grid3x3Icon, PencilIcon, Trash2Icon } from 'lucide-react';
import * as React from 'react';

import { DesignThumbnail } from './design-thumbnail';

import { t } from '@/lib/i18n';

/**
 * Lista de diseños guardados: miniatura, nombre, dimensiones, fecha (RF-35 de
 * 001) y tejido, con abrir, renombrar, duplicar y eliminar (RF-37, RF-38).
 */
export interface DesignListSheetProps {
  readonly open: boolean;
  readonly designs: readonly PatternSummaryDto[];
  readonly onOpen: (id: EntityId) => void;
  readonly onRename: (id: EntityId, name: string) => void;
  readonly onDuplicate: (id: EntityId, name: string) => void;
  readonly onDelete: (summary: PatternSummaryDto) => void;
  readonly onClose: () => void;
}

/**
 * El tejido se anuncia con ícono **y** palabra: un símbolo solo obliga a
 * aprenderlo, y el color solo no basta (RF-08).
 */
function PatternTypeBadge({ patternType }: { readonly patternType: PatternType }) {
  const staggered = patternType === 'peyote';
  const Icon = staggered ? BrickWallIcon : Grid3x3Icon;

  return (
    <span className="text-muted-foreground flex items-center gap-1 text-xs">
      <Icon className="size-3.5" aria-hidden="true" />
      {staggered ? t('patternTypePeyote') : t('patternTypeLoom')}
    </span>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

export function DesignListSheet({
  open,
  designs,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  onClose,
}: DesignListSheetProps) {
  const [renamingId, setRenamingId] = React.useState<EntityId | null>(null);
  const [draftName, setDraftName] = React.useState('');

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setRenamingId(null);
          onClose();
        }
      }}
    >
      <SheetContent side="bottom" className="max-h-[85svh] overflow-y-auto pb-8">
        <SheetHeader>
          <SheetTitle>{t('myDesigns')}</SheetTitle>
          {designs.length === 0 && <SheetDescription>{t('noDesignsYet')}</SheetDescription>}
        </SheetHeader>

        <ul className="flex flex-col gap-2 px-4 pb-2">
          {designs.map((design) => (
            <li key={design.id} className="border-border rounded-lg border p-2">
              {renamingId === design.id ? (
                <div className="flex flex-col gap-2">
                  <Input
                    className="h-11"
                    autoFocus
                    value={draftName}
                    aria-label={t('designName')}
                    onChange={(event) => {
                      setDraftName(event.target.value);
                    }}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="touch"
                      className="flex-1"
                      disabled={draftName.trim().length === 0}
                      onClick={() => {
                        onRename(design.id, draftName);
                        setRenamingId(null);
                      }}
                    >
                      {t('confirm')}
                    </Button>
                    <Button
                      variant="outline"
                      size="touch"
                      className="flex-1"
                      onClick={() => {
                        setRenamingId(null);
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    onClick={() => {
                      onOpen(design.id);
                    }}
                  >
                    <DesignThumbnail
                      thumbnail={design.thumbnail}
                      staggered={design.patternType === 'peyote'}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{design.name}</span>
                      <span className="text-muted-foreground block text-xs">
                        {t('designMeta', {
                          columns: design.columns,
                          rows: design.rowCount,
                          date: formatDate(design.updatedAt),
                        })}
                      </span>
                      <PatternTypeBadge patternType={design.patternType} />
                    </span>
                  </button>

                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="touch-icon"
                      aria-label={`${t('rename')}: ${design.name}`}
                      onClick={() => {
                        setRenamingId(design.id);
                        setDraftName(design.name);
                      }}
                    >
                      <PencilIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="touch-icon"
                      aria-label={`${t('duplicate')}: ${design.name}`}
                      onClick={() => {
                        onDuplicate(design.id, `${design.name} (2)`);
                      }}
                    >
                      <CopyIcon />
                    </Button>
                    <Button
                      variant="ghost"
                      size="touch-icon"
                      className="text-destructive"
                      aria-label={`${t('delete')}: ${design.name}`}
                      onClick={() => {
                        onDelete(design);
                      }}
                    >
                      <Trash2Icon />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
